import React from "react";
import { TableRowData } from "./Table.types";

export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
}

export interface SortConfig {
  column: string;
  direction: "asc" | "desc";
  priority: number;
}

export interface TableProps {
  columns: TableColumn[];
  data: TableRowData[];
  selectedRowId?: string;
  selectedRowIds?: string[]; // Multi-select için
  onRowClick?: (id: string, event?: React.MouseEvent) => void; // Event parametresi eklendi
  theme?: "light" | "dark";
  sortConfigs?: SortConfig[];
  onSortChange?: (column: string, isCtrlPressed: boolean) => void;
}

const Table: React.FC<TableProps> = ({
  columns,
  data,
  selectedRowId,
  selectedRowIds = [], // Multi-select için
  onRowClick,
  theme = "dark",
  sortConfigs = [],
  onSortChange,
}) => {
  const containerStyle: React.CSSProperties = {
    width: "768px",
    height: "100%",
    transform: "rotate(0deg)",
    opacity: 1,
    backgroundColor: theme === "dark" ? "#171215" : "#FFFFFF",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    fontFamily: "Inter, sans-serif",
    overflow: "hidden",
  };

  const getColumnStyle = (columnKey: string): React.CSSProperties => {
    const widths: Record<string, string> = {
      callsign: "140px",
      type: "160px",
      classification: "160px",
      latitude: "140px",
      longitude: "140px",
    };

    return {
      width: widths[columnKey] || "100px",
      minWidth: widths[columnKey] || "100px",
      padding: "8px",
      display: "flex",
      alignItems: "center",
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "20px",
      color: theme === "dark" ? "#FFFFFF" : "#000000",
      whiteSpace: "nowrap",
    };
  };

  const getClassificationStyle = (
    classification: string
  ): React.CSSProperties => {
    const colors: Record<string, string> = {
      friendly: "#32CD32",
      neutral: "#4D4DFF",
      unknown: "#B2BEB5",
      hostile: "#FF2400",
    };

    return {
      color: colors[classification.toLowerCase()] || "#FFFFFF",
      fontWeight: 600,
    };
  };

  const getClassificationDotColor = (classification: string): string => {
    const dotColors: Record<string, string> = {
      friendly: "#32CD32",
      neutral: "#4D4DFF",
      unknown: "#B2BEB5",
      hostile: "#FF2400",
    };

    return dotColors[classification.toLowerCase()] || "#FFFFFF";
  };

  const getCellContent = (key: string, row: TableRowData) => {
    const value = row[key as keyof TableRowData];

    if (key === "classification") {
      const style = getClassificationStyle(value as string);
      const dotColor = getClassificationDotColor(value as string);

      return (
        <>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: dotColor,
              marginRight: 6,
            }}
          />
          <span style={style}>{value}</span>
        </>
      );
    }

    if (key === "latitude" || key === "longitude") {
      return parseFloat(String(value)).toFixed(4);
    }

    if (key === "altitude") {
      return value ? `${value} ft` : "-";
    }

    if (key === "heading") {
      return value ? `${value}°` : "-";
    }

    if (key === "speed") {
      return value ? `${value} kts` : "-";
    }

    if (key === "lastUpdate") {
      if (!value) return "-";
      const date = new Date(Number(value));
      return date.toLocaleTimeString();
    }

    return value as string;
  };

  const getSortConfig = (columnKey: string) => {
    return sortConfigs.find((config) => config.column === columnKey);
  };

  const handleHeaderClick = (column: TableColumn, event: React.MouseEvent) => {
    if (!column.sortable || !onSortChange) return;

    const isCtrlPressed = event.ctrlKey || event.metaKey;
    onSortChange(column.key, isCtrlPressed);
  };

  const getSortIndicator = (columnKey: string) => {
    const sortConfig = getSortConfig(columnKey);
    if (!sortConfig) return null;

    const arrowIcon =
      sortConfig.direction === "asc"
        ? "/icons/arrow-up.svg"
        : "/icons/arrow-down.svg";
    const priorityText =
      sortConfigs.length > 1 ? ` ${sortConfig.priority}` : "";

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
        <img
          src={arrowIcon}
          alt={sortConfig.direction === "asc" ? "↑" : "↓"}
          width={12}
          height={12}
        />
        {priorityText && (
          <span style={{ fontSize: "10px", fontWeight: 600 }}>
            {priorityText}
          </span>
        )}
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          height: 40,
        }}
      >
        {columns.map((col) => (
          <div
            key={col.key}
            style={{
              ...getColumnStyle(col.key),
              fontWeight: 700,
              color: "#5876EE",
              gap: "4px",
              cursor: col.sortable ? "pointer" : "default",
              justifyContent: "space-between",
            }}
            onClick={(e) => handleHeaderClick(col, e)}
          >
            <span>{col.label}</span>
            {col.sortable && getSortIndicator(col.key)}
          </div>
        ))}
      </div>

      {/* Rows */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE/Edge
          position: "relative", // rectangle için
        }}
        className="custom-scroll-container"
      >
        {/* Şeffaf sabit rectangle */}
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "5px", // 8px'den 5px'e
            height: "60px", // 40px'den 60px'e
            backgroundColor: "transparent", // Şeffaf arka plan
            borderRadius: "4px",
            border: "1px solid #424242", // Border eklendi
            opacity: 0.7, // 0.8'den 0.7'ye
            pointerEvents: "none",
            zIndex: 2,
            margin: "4px",
          }}
        />

        {data.map((row, idx) => (
          <div
            key={row.id}
            style={{
              display: "flex",
              cursor: "pointer",
              backgroundColor: selectedRowIds.includes(row.id)
                ? "#BDC311" // Multi-select color (green-yellow)
                : selectedRowId === row.id
                ? "#c6c616" // Single select color (yellow)
                : idx % 2 === 1
                ? theme === "dark"
                  ? "#1D1B1D"
                  : "#F5F5F5"
                : theme === "dark"
                ? "transparent"
                : "#FFFFFF",
              borderTop: selectedRowIds.includes(row.id) 
                ? "1px solid rgba(255, 255, 255, 0.2)" 
                : "none",
              borderBottom: selectedRowIds.includes(row.id) 
                ? "1px solid rgba(255, 255, 255, 0.2)" 
                : "none",
            }}
            onClick={(event) => onRowClick?.(row.id, event)} // Event parametresi eklendi
          >
            {columns.map((col) => (
              <div key={col.key} style={getColumnStyle(col.key)}>
                {" "}
                {/* Sabit genişlik kullan */}
                {getCellContent(col.key, row)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
export default Table;
